import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Home, Ticket, Users, Trophy, LogOut, Mail, Handshake, Heart, LayoutDashboard, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const MobileMenu = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate('/');
  };

  // Menu items - Sans "À propos", "Nos Prix", "Comment participer" (ils sont dans le header)
  const menuItems = [
    { icon: Home, label: t('nav.home'), path: '/', auth: false },
    { icon: LayoutDashboard, label: t('nav.dashboard'), path: '/dashboard', auth: true },
    { icon: Ticket, label: t('nav.buyTickets'), path: '/purchase', auth: true },
    { icon: Users, label: t('nav.affiliate'), path: '/affiliate', auth: true },
    { icon: Trophy, label: t('nav.prizes'), path: '/prizes', auth: false },
    { icon: Handshake, label: t('nav.partners'), path: '/partners', auth: false, highlight: true },
    { icon: Heart, label: t('nav.donate'), path: '/donate', auth: false, special: true },
    { icon: Mail, label: t('footer.contact'), path: '/contact', auth: false },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
          <Menu className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 bg-white p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <img 
              src="/logo-marathon-2026.png" 
              alt="Marathon de l'Avenir EDENLIFE 2026" 
              className="h-12 w-auto"
            />
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* User Info */}
          {user && (
            <div className="mx-4 mt-4 p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl">
              <p className="text-sm text-gray-600">{t('nav.connectedAs')}</p>
              <p className="font-semibold text-primary truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item) => {
              if (item.auth && !user) return null;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    item.special 
                      ? 'bg-red-500 text-white hover:bg-red-600 shadow-md' 
                      : item.highlight 
                        ? 'bg-primary/10 text-primary hover:bg-primary/20'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${item.special ? 'text-white' : item.highlight ? 'text-primary' : 'text-gray-500'}`} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer - Login/Logout Button */}
          <div className="p-4 border-t bg-gray-50">
            {user ? (
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full justify-center text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 hover:border-red-300 h-12 rounded-full"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5 mr-2" />
                {t('nav.logout')}
              </Button>
            ) : (
              <Link to="/auth" onClick={() => setOpen(false)} className="block">
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-white h-12 rounded-full shadow-md"
                  data-testid="mobile-login-btn"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
